import { useEffect, useState } from "react";

import { VisuallyHidden } from "metabase/ui";

export type LiveRegionAnnouncerProps = {
  message: string;
  type?: "status" | "alert";
};

/**
 * A screen reader only ever announces a *change* to an already-registered
 * live region -- if the region and its message both appear in the same DOM
 * commit (the common case: a component that's already loading/erroring on
 * its very first render), most screen readers won't announce anything,
 * since there's no "before" state for them to have observed. Setting the
 * message from a plain effect (rather than inline during render) defers it
 * until after React's initial commit -- in a real browser that's after
 * paint, giving assistive tech a chance to register the (empty) region
 * first; deliberately not requestAnimationFrame, which -- unlike a plain
 * effect -- React Testing Library's `act()` doesn't flush synchronously,
 * so it would silently break any test asserting on this message.
 *
 * Only use this where there's no equivalent *visible* text already -- if
 * there is, put the same role/aria-live directly on that visible element
 * instead (with its own text deferred the same way): duplicating the text
 * into a second, hidden node makes every `getByText`/`findByText` assertion
 * against it ambiguous once both nodes are populated.
 *
 * Callers should render this unconditionally (not swapped in/out based on
 * loading/error state) so the region itself persists across state changes,
 * passing `""` for `message` when there's nothing to announce.
 */
export function LiveRegionAnnouncer({
  message,
  type = "status",
}: LiveRegionAnnouncerProps) {
  const [announced, setAnnounced] = useState("");

  useEffect(() => {
    setAnnounced(message);
  }, [message]);

  return (
    <VisuallyHidden
      role={type}
      aria-live={type === "alert" ? "assertive" : "polite"}
    >
      {announced}
    </VisuallyHidden>
  );
}
